import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getMessages, sendMessage, markMessagesRead } from '../services/messagesService';
import { toastError } from '../ui/toast';
import { colors as theme } from '../theme';

const c = {
  bg: theme.bg, card: theme.surface1, border: theme.surface3,
  text: theme.text, silver: theme.textMuted, gold: theme.gold,
};

const fmtTime = (iso) => new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

const CoachMessagesScreen = ({ userId, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const listRef = useRef(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getMessages(userId);
      setMessages(data);
      await markMessagesRead(userId, 'client');
    } catch (err) {
      toastError('Could not load messages: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !userId) return;
    setSending(true);
    try {
      await sendMessage(userId, 'client', text);
      // Cleared only once it has gone: a failed send keeps what the client wrote (14 October 2026)
      setDraft('');
      await load();
    } catch (err) {
      toastError('Could not send message, your text is still there: ' + (err.message || 'Unknown error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ minHeight: '100dvh', background: c.bg, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.text, padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
        >
          ← Back
        </button>
        <h1 style={{ color: c.text, margin: 0, fontSize: '18px' }}>Messages</h1>
        <button
          onClick={load}
          disabled={loading}
          style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: '8px', padding: '8px 12px', color: c.silver, fontSize: '12px', cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? '…' : '↻'}
        </button>
      </div>

      {/* Thread */}
      <div ref={listRef} className="coach-messages-thread" style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {loading && messages.length === 0 ? (
          <div style={{ margin: 'auto', color: c.silver, fontSize: '14px' }}>Loading…</div>
        ) : messages.length === 0 ? (
          <div style={{ margin: 'auto', color: c.silver, fontSize: '14px', textAlign: 'center', padding: '0 24px', lineHeight: 1.6, maxWidth: '360px' }}>
            <div style={{ fontSize: '28px', marginBottom: '10px' }} aria-hidden="true">💬</div>
            <div style={{ color: c.text, fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>
              Your direct line to your coach
            </div>
            Ask about an exercise, tell them how a session felt, or flag anything that hurts. Write
            your first message below.
          </div>
        ) : (
          messages.map(msg => {
            const isMe = msg.sender_role === 'client';
            return (
              <div key={msg.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '75%' }}>
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    backgroundColor: isMe ? c.gold : c.card,
                    color: isMe ? c.bg : c.text,
                    fontSize: '14px',
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                  }}>
                    {msg.message}
                  </div>
                  <div style={{
                    fontSize: '11px', color: c.silver, marginTop: '4px',
                    textAlign: isMe ? 'right' : 'left',
                  }}>
                    {fmtTime(msg.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer — bottom offset shifts above BottomNav's ~70px height on mobile (BottomNav hides above 768px) */}
      <div className="coach-messages-composer" style={{
        position: 'fixed', left: 0, right: 0,
        display: 'flex', gap: '8px', padding: '12px 16px',
        backgroundColor: c.card, borderTop: `1px solid ${c.border}`,
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
      }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Message your coach…"
          disabled={sending}
          style={{
            flex: 1, padding: '12px 14px', backgroundColor: c.bg,
            border: `1px solid ${c.border}`, borderRadius: '8px',
            color: c.text, fontSize: '14px', outline: 'none',
          }}
        />
        <button
          onClick={handleSend}
          disabled={sending || !draft.trim()}
          style={{
            padding: '12px 20px', borderRadius: '8px', border: 'none',
            backgroundColor: c.gold, color: c.bg, fontSize: '14px', fontWeight: 700,
            cursor: sending || !draft.trim() ? 'not-allowed' : 'pointer',
            opacity: sending || !draft.trim() ? 0.6 : 1,
          }}
        >
          Send
        </button>
      </div>

      <style>{`
        .coach-messages-thread { padding-bottom: 90px; }
        .coach-messages-composer { bottom: 0; }
        @media (max-width: 768px) {
          .coach-messages-thread { padding-bottom: 160px; }
          .coach-messages-composer { bottom: 70px; }
        }
      `}</style>
    </div>
  );
};

export default CoachMessagesScreen;
